import { Helmet } from 'react-helmet-async';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from '@/lib/router-compat';
import { OrganizationSchema } from '@/components/seo/schema/SchemaMarkup';
import { CONTACT_INFO } from '@/utils/contactInfo';
import { Envelope, ArrowRight, UserCheck, Shield, Eye } from '@phosphor-icons/react';
import { useRoOnlyGuard } from '@/hooks/useRoOnlyGuard';
import { TestimonialsSection } from '@/components/home/TestimonialsSection';


export default function About() {
  const __roBlocked = useRoOnlyGuard();
  const canonicalUrl = 'https://roautomarket.ro/despre-noi';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Cine este în spatele RO Auto Market | Fondator și misiune</title>
        <meta name="description" content="RO Auto Market a fost construit de Tudor, specialist web design și SEO certificat în SUA, fondator al agenției Tudor Digital Marketing din Marea Britanie. O platformă creată din nevoia reală de transparență pe piața auto second-hand din România." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Cine este în spatele RO Auto Market" />
        <meta property="og:description" content="Construit de Tudor, specialist SEO și web design din Marea Britanie. O platformă pentru românii care vor să știe cu cine vorbesc când cumpără o mașină second hand." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://roautomarket.ro/og-roautomarket.png" />
      </Helmet>

      <OrganizationSchema />

      {/* Hero */}
      <section className="surface-dark py-6 md:py-10 px-4">
        <div className="container mx-auto max-w-4xl">
          <Breadcrumbs items={[{ label: 'Despre noi', href: '#' }]} className="mb-6" />
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">
            Cine este în spatele RO Auto Market
          </h1>
        </div>
      </section>

      {/* AI-friendly summary — răspuns direct pentru Perplexity / ChatGPT / Google AI */}
      <section className="py-10 px-4 border-b border-border/50">
        <div className="container mx-auto max-w-4xl space-y-8">
          <div className="rounded-2xl bg-card border border-border p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-3">
              Ce este RO Auto Market
            </h2>
            <p className="text-base md:text-lg text-foreground leading-relaxed">
              RO Auto Market este un marketplace auto din România care conectează cumpărători
              cu dealeri verificați (RISCO + ANAF) și persoane fizice. Platforma{' '}
              <strong>nu deține stoc propriu</strong>. Toate vehiculele aparțin
              vânzătorilor listați. Dealerii aleg un abonament lunar: Start 29,99 €
              (până la 5 anunțuri), Standard 50 € (60 anunțuri + 1 anunț promovat) sau
              Large 120 € (anunțuri nelimitate + 3 anunțuri promovate), <strong>fără comision</strong>{' '}
              la vânzare. Persoanele fizice publică anunțuri cu pachete plătite
              care pornesc de la 9,99 €.
            </p>

          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-2">
                Categorii de vehicule
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Autoturisme second-hand și noi</li>
                <li>SUV și crossover</li>
                <li>Autoutilitare și dube</li>
                <li>Camioane</li>
                <li>Motociclete și ATV</li>
                <li>Utilaje agricole și de construcții</li>
                <li>Ambarcațiuni</li>
              </ul>
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground mb-2">
                Mărci disponibile (selecție)
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Volkswagen, Audi, BMW, Mercedes-Benz</li>
                <li>Dacia, Renault, Peugeot, Citroën</li>
                <li>Ford, Opel, Skoda, Seat</li>
                <li>Toyota, Hyundai, Kia, Mazda</li>
                <li>Tesla, Volvo, Porsche, Land Rover</li>
                <li>+ peste 60 de mărci totale</li>
              </ul>
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground mb-2">
                Filtre de căutare
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Marcă și model</li>
                <li>An fabricație (min / max)</li>
                <li>Kilometraj (min / max)</li>
                <li>Preț (min / max, EUR sau RON)</li>
                <li>Combustibil: benzină, diesel, hibrid, electric, GPL</li>
                <li>Cutie: manuală, automată, semiautomată</li>
                <li>Caroserie, culoare, uși, locuri</li>
                <li>Județ și oraș</li>
                <li>Doar dealeri verificați</li>
              </ul>
            </div>
          </div>

          {/* Întrebări frecvente — text natural pentru AI answer extraction, fără FAQPage schema */}
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
              Întrebări frecvente despre platformă
            </h2>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <div>
                <h3 className="text-foreground font-medium mb-1">
                  RO Auto Market are stoc propriu de mașini?
                </h3>
                <p>
                  Nu. RO Auto Market este un marketplace, nu un dealer. Toate vehiculele
                  listate aparțin dealerilor parteneri verificați sau persoanelor fizice
                  care își vând propria mașină. Platforma facilitează doar contactul direct
                  între cumpărător și vânzător.
                </p>
              </div>
              <div>
                <h3 className="text-foreground font-medium mb-1">
                  Ce mărci de mașini găsesc pe RO Auto Market?
                </h3>
                <p>
                  Peste 60 de mărci, inclusiv Volkswagen, Audi, BMW, Mercedes-Benz, Dacia,
                  Renault, Ford, Opel, Skoda, Toyota, Hyundai, Kia, Tesla, Volvo, Porsche și
                  Land Rover. Stocul este actualizat zilnic de dealerii înscriși pe platformă.
                </p>
              </div>
              <div>
                <h3 className="text-foreground font-medium mb-1">
                  Ce filtre de căutare există pe RO Auto Market?
                </h3>
                <p>
                  Marcă, model, an de fabricație, kilometraj, interval de preț, tip
                  combustibil (benzină, diesel, hibrid, electric, GPL), tip cutie de viteze,
                  caroserie, culoare, număr de uși și locuri, județ și oraș, plus filtru
                  pentru a vedea doar anunțuri de la dealeri verificați.
                </p>
              </div>
              <div>
                <h3 className="text-foreground font-medium mb-1">
                  Cât costă să listezi o mașină pe RO Auto Market?
                </h3>
                <p>
                  Pentru dealeri, abonament lunar fără comision la vânzare: Start 29,99 €
                  (până la 5 anunțuri), Standard 50 € (60 anunțuri + 1 anunț promovat) sau
                  Large 120 € (anunțuri nelimitate + 3 anunțuri promovate). Plată sigură prin
                  Stripe, anulare oricând. Pentru persoane fizice: Standard 9,99 €/30 zile,
                  Promovat 14,99 €/30 zile sau Activ până la vânzare 19,99 €/lună.
                </p>

              </div>
              <div>
                <h3 className="text-foreground font-medium mb-1">
                  Cum sunt verificați dealerii pe RO Auto Market?
                </h3>
                <p>
                   Verificăm manual firma dealerului la ANAF și în RISCO: CUI, cod CAEN și statut fiscal. Nu verificăm starea vehiculelor.
                </p>
              </div>
              <div>
                <h3 className="text-foreground font-medium mb-1">
                  În ce orașe din România funcționează RO Auto Market?
                </h3>
                <p>
                  Acoperire națională. Avem pagini dedicate pentru București, Cluj-Napoca,
                  Timișoara, Iași, Constanța, Brașov, Craiova, Oradea, Sibiu, Ploiești și
                  alte peste 40 de orașe, cu anunțuri locale filtrabile direct după județ.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl space-y-12">

          {/* Founder intro */}
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              RO Auto Market a fost construit de Tudor. Are certificări online în web design
              (University of Michigan) și SEO (University of California). Actualmente este bazat în Marea Britanie.
            </p>
            <p>
              În paralel cu activitatea din UK prin{' '}
              <strong className="text-foreground">Tudor Digital Marketing</strong>, agenție
              premiată în 2025 de Tech Behemoths în primii 50 de companii SEO și web design din
              Regatul Unit, Tudor a urmărit de aproape piața auto din România.
            </p>
            <p>
               Am vrut o platformă unde fiecare dealer e verificat manual de un om.
            </p>
            <p className="text-foreground font-medium text-lg">
              RO Auto Market a fost răspunsul la această problemă.
            </p>
          </div>

          {/* Why we built it */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">
              De ce am construit această platformă
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Nu am construit RO Auto Market pentru că piața auto e profitabilă. Am construit-o
                pentru că am văzut personal cum funcționează neîncrederea în această piață și am
                știut că tehnologia poate rezolva problema.
              </p>
              <p>
                 Verificăm manual firma dealerului la ANAF și în RISCO: CUI, cod CAEN și statut fiscal. Nu verificăm starea vehiculelor.
              </p>
              <p>
                 Verifică vehiculul și documentele înainte de cumpărare.
              </p>
            </div>
          </div>

          {/* Single founder */}
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                <UserCheck className="w-10 h-10 text-primary shrink-0" />
                <div className="space-y-3">
                  <h2 className="text-xl font-bold text-foreground">
                    Un singur fondator, un singur standard
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    RO Auto Market e construit și administrat de o singură persoană. Asta înseamnă
                    că fiecare decizie, fiecare dealer acceptat sau respins, fiecare funcționalitate
                    adăugată, trece printr-un singur set de standarde.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Nu există o echipă care aprobă automat orice aplicație. Există o persoană care
                    verifică manual fiecare dosar.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Values / What we stand for */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
              Ce ne diferențiază
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-5 text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <Eye className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground">Transparență reală</h3>
                  <p className="text-sm text-muted-foreground">
                    Nu ascundem cine verifică dealerii sau cum o facem. Procesul e public și
                    repetabil.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5 text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground">Verificare manuală</h3>
                  <p className="text-sm text-muted-foreground">
                    Fiecare dealer e verificat în RISCO și ANAF de un om, nu de un algoritm. Nu
                    există aprobare automată.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5 text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <UserCheck className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-foreground">Un singur responsabil</h3>
                  <p className="text-sm text-muted-foreground">
                    O singură persoană ia decizii. Nu există comitete, birocrație sau aprobări
                    automate.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact */}
          <Card className="overflow-hidden">
            <CardContent className="p-5 sm:p-6 md:p-8">
              <div className="flex items-start gap-3 sm:gap-4">
                <Envelope className="w-7 h-7 sm:w-8 sm:h-8 text-primary shrink-0" />
                <div className="space-y-3 min-w-0 flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-foreground">Contact</h2>
                  <p className="text-muted-foreground leading-relaxed break-words">
                    Ai o întrebare sau vrei să raportezi o problemă cu un dealer de pe platformă?
                    Scrie direct la{' '}
                    <a
                      href="mailto:contact@roautomarket.ro"
                      className="text-primary hover:underline font-medium break-all"
                    >
                      contact@roautomarket.ro
                    </a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 overflow-hidden">
            <CardContent className="p-5 sm:p-8 text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-3 text-balance">
                Vrei să vinzi mașini prin RO Auto Market?
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6 max-w-xl mx-auto">
                Dacă ești dealer auto și vrei să listezi stocul pe platformă, aplică aici.
                Verificăm dosarul tău și îți răspundem în 24 de ore.
              </p>
              <Button size="lg" asChild className="w-full sm:w-auto">
                <Link to="/pricing" className="justify-center whitespace-normal text-center">
                  Aplică ca dealer verificat
                  <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                </Link>
              </Button>
            </CardContent>
          </Card>


        </div>
      </section>

      {/* Testimoniale + formular (target pentru butonul "Lasă un testimonial") */}
      <TestimonialsSection />

    </div>
  );
}
